// src/components/LLMPage.jsx
import React, { useEffect, useRef, useState } from "react";
import Header from "./Header";
import SearchBar from "./SearchBar";
import bgImage from "../assets/LLM_Background.png";

// Logos
import qwenLogo from "../assets/Qwen.png";
import qwenAudio from "../assets/Qwen.png"; // same image, separate import name
import internvlLogo from "../assets/Intern-vl.png";
import llavaLogo from "../assets/llava.png";

// 🔊 Audio bubble icon + attachment preview icon
import mllmAudioIcon from "../assets/mllm_audio.png";

import BASE_URL from "../config";
import "./LLMPage.css";

// ✅ Audio pipeline is exposed as "Whisper (Audio)" in the UI,
// but still sends model_id="Qwen2-Audio-7B" to the backend,
// which the /api/vlm/audio/answer route maps to Qwen2-VL-2B text VLM
const AUDIO_MODEL_ID = "Qwen2-Audio-7B";

const MODELS = [
  {
    id: "Qwen/Qwen-VL-Chat",
    name: "Qwen-VL-Chat",
    logo: qwenLogo,
    tooltip: "Qwen-VL-Chat — strong general-purpose vision-language reasoning.",
  },
  {
    id: "OpenGVLab/InternVL-Chat-V1-5",
    name: "InternVL-Chat-V1.5",
    logo: internvlLogo,
    tooltip: "InternVL-Chat-V1.5 — alternative vision-language model for comparison.",
  },
  {
    id: "llava-hf/llava-v1.6-vicuna-13b-hf",
    name: "LLaVA-NeXT",
    logo: llavaLogo,
    tooltip: "LLaVA-NeXT (proxied through Qwen2-VL) for compatibility testing.",
  },
  {
    // 🔊 Audio model — UI shows Whisper, backend still sees Qwen2-Audio-7B
    id: AUDIO_MODEL_ID,
    name: "Whisper (Audio)",
    logo: qwenAudio,
    tooltip:
      "Whisper + Qwen2-VL-2B — transcribes audio with Whisper and classifies it using a text VLM.",
  },
];

const DEFAULT_WHISPER_ENABLED = true;
const DEFAULT_WHISPER_SIZE = "small";

const PIPELINE_STEPS = [
  { key: "ingest",   label: "Parsing Inputs" },
  { key: "vision",   label: "Visual Understanding" },
  { key: "retrieve", label: "Retrieval & Context Fusion" },
  { key: "reason",   label: "Chain-of-Thought Reasoning" },
  { key: "ground",   label: "Grounding & Fact-Checking" },
  { key: "draft",    label: "Response Drafting" },
  { key: "safety",   label: "Refinement & Safety Pass" },
];

function makeSessionId() {
  try { return crypto.randomUUID(); }
  catch { return `sess-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`; }
}

const AI_GRADIENT = "linear-gradient(90deg,#60a5fa 0%,#a78bfa 55%,#34d399 100%)";

export default function LLMPage() {
  const [modelId, setModelId] = useState(MODELS[0].id);
  const selectedModel = MODELS.find((m) => m.id === modelId) || MODELS[0];

  const [busy, setBusy] = useState(false);
  const [messages, setMessages] = useState([]);
  const [liveAttachments, setLiveAttachments] = useState({ images: [], audio: [], docs: [] });
  const [statusIdx, setStatusIdx] = useState(-1);
  const [progressPct, setProgressPct] = useState(0);
  const [tokenMeta, setTokenMeta] = useState({ input: 0, output: 0 });
  const [openTranscripts, setOpenTranscripts] = useState({});

  const chatEndRef = useRef(null);
  const sessionIdRef = useRef(makeSessionId());
  const stepTimerRef = useRef(null);
  const progressTimerRef = useRef(null);
  const startTsRef = useRef(null);

  // Track object URLs we create here so we can revoke them on unmount
  const createdUrlsRef = useRef([]);

  useEffect(() => {
    const add = (attrs) => {
      const el = document.createElement("link");
      Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
      document.head.appendChild(el);
      return el;
    };
    const l1 = add({ rel: "preconnect", href: "https://fonts.googleapis.com" });
    const l2 = add({ rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "" });
    const l3 = add({
      rel: "stylesheet",
      href:
        "https://fonts.googleapis.com/css2?family=Inter:wght@400;600;800&family=Space+Grotesk:wght@500;600;700&display=swap",
    });
    return () => [l1, l2, l3].forEach((n) => n && document.head.removeChild(n));
  }, []);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages, busy]);

  useEffect(() => {
    return () => {
      if (stepTimerRef.current) clearTimeout(stepTimerRef.current);
      if (progressTimerRef.current) clearInterval(progressTimerRef.current);
      // Revoke any object URLs we created for chat bubbles
      createdUrlsRef.current.forEach((u) => {
        try { URL.revokeObjectURL(u); } catch {}
      });
    };
  }, []);

  const hasChat = messages.length > 0;

  function estimateTokens(query, files) {
    const textTokens = Math.ceil((query || "").length / 4);
    const imageTokens = (files.images?.length || 0) * 120;
    const audioTokens = (files.audio?.length || 0) * 160;
    const docTokens   = (files.docs?.length || 0)  * 200;
    return textTokens + imageTokens + audioTokens + docTokens;
  }

  function startStatusUI(query, files) {
    setStatusIdx(0);
    setProgressPct(6);
    setTokenMeta({ input: estimateTokens(query, files), output: 0 });
    startTsRef.current = Date.now();
    const STEP_MIN = 600, STEP_MAX = 1200;
    const runStep = (i) => {
      setStatusIdx(i);
      if (i < PIPELINE_STEPS.length - 1) {
        const delay = Math.floor(STEP_MIN + Math.random() * (STEP_MAX - STEP_MIN));
        stepTimerRef.current = setTimeout(() => runStep(i + 1), delay);
      }
    };
    runStep(0);
    progressTimerRef.current = setInterval(() => {
      setProgressPct((p) => {
        const cap = busy ? 92 : 100;
        const next = p + Math.max(0.4, (100 - p) * 0.03);
        return Math.min(next, cap);
      });
    }, 160);
  }

  function stopStatusUI() {
    if (stepTimerRef.current) clearTimeout(stepTimerRef.current);
    if (progressTimerRef.current) clearInterval(progressTimerRef.current);
    setStatusIdx(PIPELINE_STEPS.length - 1);
    setProgressPct(100);
    setTokenMeta((t) => ({ ...t, output: t.output || Math.round(t.input * 0.6) }));
  }

  async function copyToClipboard(text) {
    try { await navigator.clipboard.writeText(text); }
    catch (e) { console.error("Clipboard copy failed", e); }
  }

  // Clear Chat: reset all UI state and start a fresh session (also remount SearchBar via key)
  function clearChat() {
    // stop animations/timers
    if (stepTimerRef.current) clearTimeout(stepTimerRef.current);
    if (progressTimerRef.current) clearInterval(progressTimerRef.current);
    // revoke any preview URLs
    createdUrlsRef.current.forEach((u) => {
      try { URL.revokeObjectURL(u); } catch {}
    });
    createdUrlsRef.current = [];
    // reset state
    setMessages([]);
    setLiveAttachments({ images: [], audio: [], docs: [] });
    setOpenTranscripts({});
    setBusy(false);
    setStatusIdx(-1);
    setProgressPct(0);
    setTokenMeta({ input: 0, output: 0 });
    // new session id (also used as key for SearchBar to remount and clear its inputs)
    sessionIdRef.current = makeSessionId();
  }

  const handleSubmit = async ({ query, files }) => {
    if (modelId === AUDIO_MODEL_ID && files.audio.length === 0) {
      setMessages((m) => [
        ...m,
        { role: "assistant", text: "⚠️ Please attach an audio file before sending to the Audio model." },
      ]);
      return;
    }
    if (!query && files.images.length === 0 && files.audio.length === 0 && files.docs.length === 0) return;

    // build image URLs from the submitted files (not from liveAttachments)
    const userImgs = files.images.map((f) => {
      const u = URL.createObjectURL(f);
      createdUrlsRef.current.push(u);
      return u;
    });

    const hasAudio = files.audio && files.audio.length > 0;

    // user message now also carries an "hasAudio" flag so we can show mllm_audio.png in the chat
    setMessages((m) => [
      ...m,
      {
        role: "user",
        text: query || "(no text)",
        imgs: userImgs,
        hasAudio: hasAudio,
      },
    ]);

    setBusy(true);
    startStatusUI(query, files);

    const fd = new FormData();
    fd.append("question", query || "");
    fd.append("model_id", modelId);
    fd.append("session_id", sessionIdRef.current);
    files.images.forEach((f) => fd.append("image", f));
    files.audio.forEach((f) => fd.append("audio", f));
    files.docs.forEach((f) => fd.append("docs", f));

    let endpoint = "/vlm/answer";
    if (modelId === AUDIO_MODEL_ID) {
      endpoint = "/vlm/audio/answer";
      // whisper flags are read on the backend; for now we always enable small Whisper
      fd.append("whisper_size", DEFAULT_WHISPER_SIZE);
    }

    try {
      const res = await fetch(`${BASE_URL}${endpoint}`, { method: "POST", body: fd });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || data?.hint || res.statusText);

      const answer = data?.answer ?? "(no answer returned)";
      const transcript = data?.transcript;

      stopStatusUI();

      setMessages((m) => [
        ...m,
        { role: "assistant", text: answer, transcript: transcript || null },
      ]);

      setTokenMeta((t) => ({ ...t, output: t.output || Math.round(answer.length / 3.8) }));
    } catch (e) {
      stopStatusUI();
      setMessages((m) => [
        ...m,
        { role: "assistant", text: `⚠️ ${e?.message || "Request failed"}` },
      ]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Header />
      <div className="llm-scope">
        <div
          className="vlm-page-bg"
          style={{
            backgroundImage: `linear-gradient(rgba(10,15,35,0.92), rgba(10,15,35,0.92)), url(${bgImage})`,
          }}
        />
        <main
          className="vlm-shell"
          style={{
            minHeight: "calc(100vh - 120px)",
            display: "grid",
            gridTemplateRows: "auto auto auto 1fr auto",
            alignItems: "start",
            justifyItems: "center",
            gap: 18,
            paddingTop: 20,
            fontFamily:
              '"Inter", ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, "Helvetica Neue", Arial',
          }}
        >
          {/* Title */}
          <section style={{ textAlign: "center", marginTop: 8 }}>
            <h1
              style={{
                margin: 0,
                fontFamily: '"Space Grotesk", Inter, system-ui',
                fontWeight: 700,
                letterSpacing: 0.2,
                fontSize: "clamp(24px, 3.6vw, 38px)",
                lineHeight: 1.15,
                background: AI_GRADIENT,
                WebkitBackgroundClip: "text",
                backgroundClip: "text",
                color: "transparent",
                textShadow: "0 0 14px rgba(96,165,250,0.18)",
              }}
            >
              Multimodal LLM Workspace
            </h1>
            <p
              style={{
                margin: "6px 0 0",
                color: "rgba(226,238,255,0.8)",
                fontSize: "clamp(12px, 1.6vw, 14px)",
              }}
            >
              Work fluidly with <strong>text</strong>, <strong>images</strong>, <strong>audio</strong> & <strong>documents</strong> — in one place.
            </p>
          </section>

          {/* Model pills */}
          <div
            style={{
              display: "flex",
              gap: 12,
              alignItems: "center",
              flexWrap: "wrap",
              justifyContent: "center",
            }}
          >
            {MODELS.map((m) => {
              const active = modelId === m.id;
              const tooltip = m.tooltip || m.name;
              const isAudio = m.id === AUDIO_MODEL_ID;
              return (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => setModelId(m.id)}
                  aria-pressed={active}
                  title={tooltip}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    padding: "10px 14px",
                    borderRadius: 16,
                    cursor: "pointer",
                    background: active
                      ? `linear-gradient(#0b1324,#0b1324) padding-box, ${AI_GRADIENT} border-box`
                      : "linear-gradient(#0b1324,#0b1324) padding-box, linear-gradient(0deg, rgba(255,255,255,0.12), rgba(255,255,255,0.12)) border-box",
                    border: "1px solid transparent",
                    color: active ? "#eaf2ff" : "#d9e6ff",
                    boxShadow: active
                      ? "0 10px 26px rgba(96,165,250,0.18), 0 4px 14px rgba(52,211,153,0.14)"
                      : "0 2px 10px rgba(0,0,0,0.28)",
                    transform: active ? "translateY(-1px)" : "none",
                    transition: "transform 120ms ease, box-shadow 120ms ease, filter 120ms ease",
                    filter: active ? "saturate(1.05)" : "none",
                  }}
                >
                  <img
                    src={m.logo}
                    alt=""
                    aria-hidden
                    style={{ width: 20, height: 20, borderRadius: 6, objectFit: "cover" }}
                  />
                  <span style={{ fontWeight: 700, fontSize: 13 }}>{m.name}</span>
                  {isAudio && (
                    <span
                      style={{
                        marginLeft: 2,
                        fontSize: 11,
                        opacity: 0.9,
                        padding: "2px 6px",
                        borderRadius: 10,
                        border: "1px solid rgba(255,255,255,0.16)",
                        background: "rgba(15,23,42,0.9)",
                      }}
                    >
                      Whisper
                    </span>
                  )}
                </button>
              );
            })}
            <button
              type="button"
              onClick={clearChat}
              className="clear-chat-btn"
              aria-label="Clear chat"
            >
              Clear Chat
            </button>
          </div>

          {/* Composer + attachments */}
          <section
            className={`composer-row ${hasChat ? "compact" : ""}`}
            style={{ width: "min(1100px, 92vw)" }}
          >
            <div className="composer-left">
              <SearchBar
                key={sessionIdRef.current}
                onSubmit={handleSubmit}
                busy={busy}
                showMiniThumbs={false}
                onAttachmentsChange={setLiveAttachments}
              />
              <div className="composer-tip">
                Tip: Press <kbd className="kbd">/</kbd> to focus • <kbd className="kbd">Ctrl</kbd>/<kbd className="kbd">⌘</kbd><kbd className="kbd">Enter</kbd> to send
              </div>
              {modelId === AUDIO_MODEL_ID && (
                <div
                  style={{
                    marginTop: 6,
                    fontSize: 12,
                    color: "rgba(226,238,255,0.8)",
                  }}
                >
                  <strong>Audio mode:</strong> uses <span style={{ fontWeight: 700 }}>Whisper</span> to transcribe the audio, then{" "}
                  <span style={{ fontWeight: 700 }}>Qwen2-VL-2B</span> to judge whether it sounds real or fake.
                </div>
              )}
            </div>

            <aside className="attachment-box" aria-label="Attachment preview">
              {liveAttachments.images?.length || liveAttachments.audio?.length ? (
                <div style={{ display: "grid", gap: 8 }}>
                  {liveAttachments.images?.length ? (
                    <div className="attachment-grid">
                      {liveAttachments.images.map((a, i) => (
                        <div className="attachment-tile" key={`att-img-${i}`} title={a?.file?.name || ""}>
                          <img src={a.url} alt={a?.file?.name || `image-${i + 1}`} />
                        </div>
                      ))}
                    </div>
                  ) : null}
                  {liveAttachments.audio?.length ? (
                    <div
                      style={{
                        padding: 8,
                        borderRadius: 10,
                        background: "rgba(255,255,255,0.04)",
                        border: "1px solid rgba(255,255,255,0.08)",
                        color: "rgba(226,238,255,0.85)",
                        fontSize: 12,
                        display: "grid",
                        gap: 8,
                      }}
                    >
                      {/* 🔊 Audio preview tile with mllm_audio.png when Whisper is chosen */}
                      {modelId === AUDIO_MODEL_ID && (
                        <div
                          className="attachment-grid"
                          style={{ gridTemplateColumns: "repeat(1, minmax(0, 1fr))" }}
                        >
                          <div
                            className="attachment-tile"
                            title="Audio attached"
                            style={{ display: "flex", alignItems: "center", justifyContent: "center" }}
                          >
                            <img
                              src={mllmAudioIcon}
                              alt="Audio attachment preview"
                              style={{ width: "100%", height: "100%", objectFit: "contain" }}
                            />
                          </div>
                        </div>
                      )}

                      <div style={{ fontWeight: 700, marginBottom: 2 }}>Audio files</div>
                      <ul style={{ margin: 0, paddingLeft: 16 }}>
                        {liveAttachments.audio.map((a, i) => (
                          <li key={`att-aud-${i}`}>{a?.file?.name || `audio-${i + 1}`}</li>
                        ))}
                      </ul>
                    </div>
                  ) : null}
                </div>
              ) : (
                <div className="attachment-empty">
                  <div className="attachment-empty-title">No attachments yet</div>
                  <div className="attachment-empty-sub">Attach an image or audio file to see it here</div>
                </div>
              )}
            </aside>
          </section>

          {/* Chat window */}
          {hasChat && (
            <section
              className="chat-window"
              aria-live="polite"
              style={{ width: "min(980px, 88vw)" }}
            >
              {(busy || statusIdx >= 0) && (
                <div className="analysis-status">
                  <div className="status-steps">
                    {PIPELINE_STEPS.map((s, i) => {
                      const cls =
                        i < statusIdx
                          ? "status-chip done"
                          : i === statusIdx && busy
                          ? "status-chip active"
                          : "status-chip pending";
                      return (
                        <div className={cls} key={s.key} title={s.label}>
                          {i < statusIdx ? (
                            <svg viewBox="0 0 24 24" width="13" height="13" aria-hidden>
                              <path fill="currentColor" d="M9 16.2l-3.5-3.5-1.4 1.4L9 19 20 8l-1.4-1.4z" />
                            </svg>
                          ) : i === statusIdx && busy ? (
                            <span className="typing-dots" aria-hidden>
                              <span></span><span></span><span></span>
                            </span>
                          ) : (
                            <svg viewBox="0 0 24 24" width="13" height="13" aria-hidden>
                              <circle cx="12" cy="12" r="5" fill="currentColor" opacity=".6"></circle>
                            </svg>
                          )}
                          <span>{s.label}</span>
                        </div>
                      );
                    })}
                  </div>

                  <div className="status-strip" />

                  <div className="token-meter">
                    <div className="track">
                      <div
                        className="fill"
                        style={{ "--pct": `${Math.min(progressPct, 100)}%` }}
                      />
                    </div>
                    <div className="meta">
                      <span className="ai-text-gradient">
                        {busy ? "Running pipeline" : "Completed"}
                      </span>
                      <span>
                        est. tokens — in: {tokenMeta.input} • out: {tokenMeta.output} • progress: {Math.round(progressPct)}%
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {messages.map((m, idx) => (
                <article key={idx} className={`msg ${m.role}`}>
                  <div className="bubble">
                    {m.text}
                    {m.imgs?.length ? (
                      <div className="bubble-images">
                        {m.imgs.map((u, i) => (
                          <img src={u} alt={`uploaded-${i + 1}`} key={`bubble-img-${idx}-${i}`} />
                        ))}
                      </div>
                    ) : null}

                    {/* 🔊 If this user message had audio attached, show mllm_audio.png inside the bubble */}
                    {m.role === "user" && m.hasAudio && (
                      <div
                        style={{
                          marginTop: 8,
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 8,
                          padding: "6px 10px",
                          borderRadius: 10,
                          background: "rgba(15,23,42,0.9)",
                          border: "1px solid rgba(148,163,184,0.5)",
                        }}
                      >
                        <img
                          src={mllmAudioIcon}
                          alt="Audio attachment"
                          style={{
                            width: 26,
                            height: 26,
                            borderRadius: 6,
                            objectFit: "contain",
                          }}
                        />
                        <span
                          style={{
                            fontSize: 12,
                            color: "rgba(226,238,255,0.9)",
                            fontWeight: 600,
                          }}
                        >
                          Audio attached
                        </span>
                      </div>
                    )}
                  </div>

                  {m.role === "assistant" && m.transcript ? (
                    <div
                      style={{
                        marginTop: 8,
                        padding: 10,
                        borderRadius: 12,
                        border: "1px solid rgba(255,255,255,0.10)",
                        background: "rgba(255,255,255,0.04)",
                        color: "rgba(226,238,255,0.85)",
                        fontSize: 13,
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 10,
                        }}
                      >
                        <button
                          type="button"
                          onClick={() =>
                            setOpenTranscripts((o) => ({ ...o, [idx]: !o[idx] }))
                          }
                          style={{
                            background: "transparent",
                            color: "#cfe2ff",
                            border: "none",
                            cursor: "pointer",
                            fontWeight: 700,
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            padding: 0,
                          }}
                          aria-expanded={!!openTranscripts[idx]}
                        >
                          <span
                            className="ai-text-gradient"
                            style={{
                              background: AI_GRADIENT,
                              WebkitBackgroundClip: "text",
                              backgroundClip: "text",
                              color: "transparent",
                            }}
                          >
                            {openTranscripts[idx] ? "Hide transcript" : "Show transcript"}
                          </span>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
                            <path
                              d={openTranscripts[idx] ? "M7 14l5-5 5 5" : "M7 10l5 5 5-5"}
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </button>

                        <button
                          type="button"
                          onClick={() => copyToClipboard(m.transcript)}
                          title="Copy Transcript"
                          style={{
                            padding: "6px 10px",
                            fontSize: 12,
                            fontWeight: 800,
                            borderRadius: 10,
                            border: "1px solid transparent",
                            background: `linear-gradient(#0b1324,#0b1324) padding-box, ${AI_GRADIENT} border-box`,
                            color: "#eaf2ff",
                            cursor: "pointer",
                            boxShadow:
                              "0 6px 16px rgba(96,165,250,0.18), 0 3px 10px rgba(52,211,153,0.12)",
                          }}
                        >
                          Copy Transcript
                        </button>
                      </div>

                      {openTranscripts[idx] ? (
                        <div
                          style={{
                            marginTop: 8,
                            padding: "10px 12px",
                            borderRadius: 10,
                            background: "rgba(255,255,255,0.03)",
                            border: "1px dashed rgba(255,255,255,0.12)",
                            whiteSpace: "pre-wrap",
                            lineHeight: 1.5,
                          }}
                        >
                          {m.transcript}
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                </article>
              ))}

              {busy && (
                <article className="msg assistant">
                  <div className="bubble thinking">
                    <span className="ai-text-gradient">Analyzing request</span>{" "}
                    <span className="typing-dots" aria-hidden>
                      <span></span><span></span><span></span>
                    </span>
                  </div>
                </article>
              )}

              <div ref={chatEndRef} />
            </section>
          )}

          {/* Footer */}
          <div
            style={{
              marginTop: 8,
              marginBottom: 18,
              padding: "8px 12px",
              borderRadius: 12,
              background: "rgba(255,255,255,0.04)",
              border: "1px solid rgba(255,255,255,0.08)",
              color: "rgba(226,238,255,0.7)",
              fontSize: 12,
            }}
          >
            Powered by{" "}
            <span
              style={{
                background: AI_GRADIENT,
                WebkitBackgroundClip: "text",
                backgroundClip: "text",
                color: "transparent",
                fontWeight: 700,
              }}
            >
              {selectedModel.name}
            </span>
          </div>
        </main>
      </div>
    </>
  );
}
